import React, { useRef, useEffect, type FC, useState } from "react";
import SearchSuggestionList from "./SearchSuggestionList";
import ArrowRightIcon from "../../../components/icons/ArrowRightIcon";
import type { ErrorReason } from "../../../core/services/fileUploadService";
import FilePreview, { FileUpload } from "../../../components/FilePreview";
import PlusIcon from "../../../components/icons/PlusIcon";
import { PageContext } from "../../../types/page";
import { useTranslation } from "../../../contexts/useTranslation";
import { useCore } from "../../../contexts/useCore";
import { useWidget } from "../../../contexts/useWidget";

interface ChatFormProps {
  onSubmit: (pageContext: PageContext) => void;
}

const initialFileUpload: FileUpload = {
  file: null,
  id: null,
  error: null,
  progress: 0
};

const TEXTAREA_HEIGHT = { min: 50, max: 220 };

const ChatForm: FC<ChatFormProps> = ({ onSubmit }) => {
  const { translate } = useTranslation();
  const { attachmentsEnabled } = useWidget();
  const core = useCore();
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [userInput, setUserInput] = useState<string>("");
  const [previousSearchQuery, setPreviousSearchQuery] = useState<string | null>(null);
  const [fileUpload, setFileUpload] = useState<FileUpload>(initialFileUpload);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);

  const resetFileUpload = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
      uploadAbortRef.current = null;
    }
    setFileUpload(initialFileUpload);
  };

  const resizeTextarea = (): void => {
    if (textareaRef.current) {
      // Resets the height to shrink if necessary
      textareaRef.current.style.height = `${TEXTAREA_HEIGHT.min}px`;

      // Adjusts the height according to the scroll height
      const newHeight = Math.min(textareaRef.current.scrollHeight + 2, TEXTAREA_HEIGHT.max);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onSubmit({ message: userInput, fileUpload });
  };

  const handleTextareaPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>): void => {
    // With attachments off, a pasted file is ignored; text pastes as usual.
    if (!attachmentsEnabled) return;
    const item = event.clipboardData.items[0]; // Takes only the first clipboard item
    if (item && item.kind === "file") {
      event.preventDefault();
      setFileUpload({ ...initialFileUpload, file: item.getAsFile()! });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    setFileUpload({ ...initialFileUpload, file: file ?? null });
  };

  const handleFileAttach = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    const handleTextareaKeyDown = (event: KeyboardEvent): void => {
      if (submitEnabled && event.key === "Enter" && !event.shiftKey) {
        event.preventDefault(); // Prevents the default behavior of adding a new line
        formRef.current?.requestSubmit();
      } else if (event.key === "Tab") {
        event.preventDefault(); // Prevent focus from moving
        if (searchSuggestions.length > 0) {
          setUserInput(searchSuggestions[0]);
        }
      }
    };

    if (textareaRef.current) {
      textareaRef.current.addEventListener("keydown", handleTextareaKeyDown);
    }

    return () => {
      if (textareaRef.current) {
        textareaRef.current.removeEventListener("keydown", handleTextareaKeyDown);
      }
    };
  }, [submitEnabled, searchSuggestions]);

  useEffect(() => {
    // Textarea needs to grow and shrink based on the user's input
    resizeTextarea();
  }, [userInput]);

  useEffect(() => {
    // Enable submit button if the form is filled
    const trimmedUserInput = userInput.trim();
    setSubmitEnabled(trimmedUserInput.length > 0);

    // Fetch suggestions when input changes
    const fetchSuggestions = async (): Promise<void> => {
      // Store the previous search query to avoid making the same request again
      setPreviousSearchQuery(trimmedUserInput);
      try {
        const suggestions = await core.getSearchSuggestions(trimmedUserInput);
        setSearchSuggestions(suggestions);
      } catch {
        setSearchSuggestions([]);
      }
    };

    // Debounce the API call to avoid too many requests and query only if the user input has changed
    const timeoutId = previousSearchQuery !== trimmedUserInput ? setTimeout(fetchSuggestions, 500) : undefined;

    // Cleanup timeout on next effect or unmount
    return () => clearTimeout(timeoutId);
  }, [userInput]);

  useEffect(() => {
    const getErrorMessage = (errorReason: ErrorReason): string => {
      switch (errorReason.error_type) {
        case "content_type_not_supported":
          return translate("content_type_not_supported");
        case "file_size_too_large":
          return translate("file_size_too_large", { max: errorReason.metadata.max });
        default:
          return translate("upload_failed_due_to_unknown_error");
      }
    };

    const uploadFile = async (file: File): Promise<void> => {
      const abort = new AbortController();
      uploadAbortRef.current = abort;
      try {
        const { id } = await core.attachFile(file, {
          signal: abort.signal,
          onProgress: (progress: number) => {
            setFileUpload(prevFileUpload => ({ ...prevFileUpload, progress }));
          },
        });
        uploadAbortRef.current = null;
        setFileUpload(prevFileUpload => ({ ...prevFileUpload, id }));
      } catch (err) {
        uploadAbortRef.current = null;
        if (abort.signal.aborted) return;
        const reason = (err as { cause?: { errorReason?: ErrorReason | null } })?.cause?.errorReason;
        resetFileUpload();
        if (reason) {
          setFileUpload({ ...initialFileUpload, error: getErrorMessage(reason) });
        }
      }
    };

    if (fileUpload.file) {
      void uploadFile(fileUpload.file);
    }

    // Abort any ongoing upload when the component unmounts
    return () => {
      if (uploadAbortRef.current) {
        uploadAbortRef.current.abort();
        uploadAbortRef.current = null;
      }
    };
  }, [fileUpload.file]);

  return (
    <>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="flex-1 flex flex-col overflow-y-hidden"
      >
        <div className="flex-1 flex flex-col gap-y-4 px-4 overflow-y-auto md:max-w-[var(--lastbot-content-max-width)] md:w-full md:self-center">
          {/* The `border-b-2` applied here means that we need to use `textareaRef.current.scrollHeight + 2` above */}
          <textarea
            ref={textareaRef}
            value={userInput}
            placeholder={`${translate("ask_anything")}...`}
            onChange={(event) => setUserInput(event.target.value)}
            onPaste={handleTextareaPaste}
            className="block w-full resize-none h-[50px] py-3 border-b-2 bg-transparent text-lastbot-on-surface placeholder:text-lastbot-on-surface-subtle focus:outline-hidden md:scrollbar-hide"
            autoFocus
            data-testid={`chat-input`}
          />
          <SearchSuggestionList
            suggestions={searchSuggestions}
            onClick={(suggestion) => onSubmit({ message: suggestion, fileUpload })}
          />
        </div>
        <div className="px-4 pb-4 md:max-w-[var(--lastbot-content-max-width)] md:w-full md:self-center">
          {fileUpload.file && (
            <div className="pb-2">
              <FilePreview
                fileUpload={fileUpload}
                onRemove={resetFileUpload}
              />
            </div>
          )}
          {/* Without the attach control the submit button keeps its place at the end. */}
          <div className={`flex gap-x-6 items-center ${attachmentsEnabled ? "justify-between" : "justify-end"} pl-4 pr-2 py-2`}>
            {/* The account can switch visitor attachments off (LAS-3469); the
                server then refuses uploads, so the control is not offered. */}
            {attachmentsEnabled && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  tabIndex={-1}
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={handleFileAttach}
                  className="text-lastbot-icon-muted cursor-pointer"
                  data-testid={`chat-attach-file-button`}
                >
                  <PlusIcon />
                </button>
              </>
            )}
            <button
              type="submit"
              disabled={!submitEnabled}
              className="bg-lastbot-send-surface text-lastbot-on-send-surface rounded-full p-2 cursor-pointer disabled:text-lastbot-disabled-on-surface disabled:cursor-not-allowed disabled:bg-lastbot-disabled-surface"
              data-testid={`chat-submit`}
            >
              <ArrowRightIcon />
            </button>
          </div>
          {fileUpload.error && (
            <div className="px-6 pt-3 text-xs text-center text-lastbot-danger-strong" data-testid={`chat-file-upload-error`}>
              {fileUpload.error}
            </div>
          )}
        </div>
      </form>
    </>
  );
};

export default ChatForm;
